/* ==========================================================================
   Slancio — global UI
   ========================================================================== */
(() => {
  'use strict';

  /* Scroll lock shared by all overlays ------------------------------------ */
  window.slancioUnlockScroll = () => {
    if (!document.querySelector('slancio-menu-drawer.is-open, [data-facets-wrapper].is-open, slancio-cart-drawer.is-open:not([data-test-mode])')) {
      document.body.classList.remove('is-locked');
    }
  };

  /* Toast ----------------------------------------------------------------- */
  window.slancioToast = (message) => {
    const el = document.querySelector('[data-toast]');
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(el._timer);
    el._timer = setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => { el.hidden = true; }, 300);
    }, 3200);
  };

  /* Close <details> menus on outside click / Escape ------------------------ */
  document.addEventListener('click', (e) => {
    document.querySelectorAll('details[data-auto-close][open]').forEach((d) => {
      if (!d.contains(e.target)) d.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('details[data-auto-close][open]').forEach((d) => {
      d.removeAttribute('open');
      const s = d.querySelector('summary');
      if (s) s.focus();
    });
  });

  /* Announcement bar rotation --------------------------------------------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.announcement-bar__item'));
      if (this.items.length < 2) return;
      this.index = 0;
      const speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      this.timer = setInterval(() => this.next(), speed);
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', () => { this.timer = setInterval(() => this.next(), speed); });
    }
    disconnectedCallback() { clearInterval(this.timer); }
    next() {
      this.items[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].classList.add('is-active');
    }
  }
  if (!customElements.get('slancio-announcement-bar')) customElements.define('slancio-announcement-bar', AnnouncementBar);

  /* Mobile menu drawer ---------------------------------------------------- */
  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-menu-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    open(opener) {
      this.opener = opener;
      this.classList.add('is-open');
      document.body.classList.add('is-locked');
      const first = this.querySelector('a, button');
      if (first) setTimeout(() => first.focus(), 100);
    }
    close() {
      this.classList.remove('is-open');
      window.slancioUnlockScroll();
      if (this.opener) this.opener.focus();
    }
  }
  if (!customElements.get('slancio-menu-drawer')) customElements.define('slancio-menu-drawer', MenuDrawer);
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-menu-toggle]');
    if (!toggle) return;
    const drawer = document.querySelector('slancio-menu-drawer');
    if (drawer) drawer.open(toggle);
  });

  /* Search toggle --------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-search-toggle]');
    if (!toggle) return;
    const modal = document.querySelector('[data-search-modal]');
    if (!modal) return;
    e.preventDefault();
    modal.hidden = !modal.hidden;
    if (!modal.hidden) modal.querySelector('input').focus();
  });

  /* Sticky header hide on scroll down ------------------------------------- */
  const header = document.querySelector('[data-sticky-header]');
  if (header) {
    let last = window.scrollY;
    const section = header.closest('.shopify-section');
    if (section) section.classList.add('shopify-section-header-sticky');
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      if (header.dataset.stickyHeader === 'reveal' && section) {
        section.style.transform = y > last && y > 200 ? 'translateY(-100%)' : '';
        section.style.transition = 'transform .3s ease';
      }
      header.classList.toggle('is-scrolled', y > 10);
      last = y;
    }, { passive: true });
  }

  /* Quantity inputs (product page) ---------------------------------------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-quantity-step]');
    if (!btn || btn.closest('[data-cart-section]')) return;
    const input = btn.closest('.quantity').querySelector('input');
    const min = parseInt(input.min, 10) || 1;
    const max = input.max ? parseInt(input.max, 10) : Infinity;
    const next = (parseInt(input.value, 10) || min) + parseInt(btn.dataset.quantityStep, 10);
    input.value = Math.min(max, Math.max(min, next));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* Product page ----------------------------------------------------------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variants-json]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.form = this.querySelector('[data-product-form]') || this.querySelector('form[action*="/cart/add"]');
      this.idInput = this.form && this.form.querySelector('input[name="id"]');
      this.addEventListener('change', (e) => {
        if (e.target.closest('[data-variant-picker]')) this.onOptionChange();
      });
      this.updateAvailability();
      this.setupStickyBar();
    }

    selectedOptions() {
      const picker = this.querySelector('[data-variant-picker]');
      if (!picker) return [];
      return Array.from(picker.querySelectorAll('[data-option]')).map((group) => {
        const checked = group.querySelector('input:checked');
        if (checked) return checked.value;
        const select = group.querySelector('select');
        return select ? select.value : null;
      });
    }

    onOptionChange() {
      const options = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((o, i) => o === options[i]));
      this.updateAvailability();
      this.querySelectorAll('[data-option]').forEach((group, i) => {
        const label = group.querySelector('[data-option-value]');
        if (label) label.textContent = options[i];
      });
      const button = this.querySelector('[data-add-button]');
      const buttonLabel = button && button.querySelector('[data-add-label]');
      if (!variant) {
        if (button) button.setAttribute('aria-disabled', 'true');
        if (buttonLabel) buttonLabel.textContent = window.theme.strings.unavailable;
        return;
      }
      if (this.idInput) this.idInput.value = variant.id;
      if (button) button.setAttribute('aria-disabled', String(!variant.available));
      if (buttonLabel) buttonLabel.textContent = variant.available ? window.theme.strings.addToCart : window.theme.strings.soldOut;

      this.querySelectorAll('[data-price]').forEach((price) => {
        const regular = price.querySelector('[data-price-regular]');
        const compare = price.querySelector('[data-price-compare]');
        const badge = price.querySelector('[data-price-badge]');
        if (regular) regular.innerHTML = variant.price;
        if (compare) { compare.innerHTML = variant.compare || ''; compare.hidden = !variant.compare; }
        if (badge) { badge.hidden = !variant.savings; badge.textContent = variant.savingsLabel || ''; }
        price.classList.toggle('price--sale', Boolean(variant.compare));
      });

      const inventory = this.querySelector('[data-inventory]');
      if (inventory && variant.inventoryHtml !== undefined) inventory.innerHTML = variant.inventoryHtml;

      const sticky = document.querySelector('[data-sticky-price]');
      if (sticky) sticky.innerHTML = variant.price;

      if (variant.mediaId) {
        const gallery = document.querySelector('slancio-product-gallery');
        if (gallery) gallery.goTo(variant.mediaId);
      }

      if (this.dataset.updateUrl !== 'false') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    updateAvailability() {
      const selected = this.selectedOptions();
      this.querySelectorAll('[data-option]').forEach((group, index) => {
        group.querySelectorAll('input[type="radio"], option').forEach((input) => {
          const candidate = selected.slice();
          candidate[index] = input.value;
          const available = this.variants.some((v) => v.available && v.options.every((o, i) => i === index ? o === input.value : (i < index ? o === candidate[i] : true)));
          input.classList.toggle('is-unavailable', !available);
        });
      });
    }

    setupStickyBar() {
      const bar = document.querySelector('[data-sticky-atc]');
      const button = this.querySelector('[data-add-button]');
      if (!bar || !button || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(([entry]) => {
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      io.observe(button);
    }
  }
  if (!customElements.get('slancio-product-info')) customElements.define('slancio-product-info', ProductInfo);

  /* Product gallery ------------------------------------------------------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(thumb.dataset.thumb)));
      if (this.track) {
        this.track.addEventListener('scroll', () => {
          clearTimeout(this.scrollTimer);
          this.scrollTimer = setTimeout(() => this.syncThumbs(), 60);
        }, { passive: true });
      }
    }
    goTo(mediaId) {
      const slide = this.querySelector(`[data-media-id="${mediaId}"]`);
      if (!slide || !this.track) return;
      if (getComputedStyle(this.track).display === 'grid') {
        slide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else {
        this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      }
      this.setActive(mediaId);
    }
    syncThumbs() {
      const slides = Array.from(this.track.children);
      const index = Math.round(this.track.scrollLeft / (this.track.clientWidth || 1));
      const slide = slides[index];
      if (slide) this.setActive(slide.dataset.mediaId);
    }
    setActive(mediaId) {
      this.thumbs.forEach((t) => {
        const active = t.dataset.thumb === String(mediaId);
        t.classList.toggle('is-active', active);
        if (active) t.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      });
    }
  }
  if (!customElements.get('slancio-product-gallery')) customElements.define('slancio-product-gallery', ProductGallery);

  /* Share button ----------------------------------------------------------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-share]');
    if (!btn) return;
    const url = btn.dataset.url || window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: document.title, url }); } catch (err) { /* dismissed */ }
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(url);
      window.slancioToast(btn.dataset.copied || 'Link copied');
    }
  });

  /* Facets: mobile drawer + auto submit ------------------------------------ */
  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-facets-open]');
    const close = e.target.closest('[data-facets-close]');
    const wrapper = document.querySelector('[data-facets-wrapper]');
    if (!wrapper) return;
    if (open) { wrapper.classList.add('is-open'); document.body.classList.add('is-locked'); }
    if (close) { wrapper.classList.remove('is-open'); window.slancioUnlockScroll(); }
  });
  document.addEventListener('change', (e) => {
    const form = e.target.closest('[data-auto-submit]');
    if (!form) return;
    if (e.target.type === 'number' || e.target.type === 'text') return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  });
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-facets-form]');
    if (!form) return;
    // Strip empty price fields for clean URLs
    form.querySelectorAll('input[type="number"]').forEach((input) => { if (input.value === '') input.disabled = true; });
  });

  /* Localization selectors -------------------------------------------------- */
  document.addEventListener('change', (e) => {
    const select = e.target.closest('[data-localization-select]');
    if (select) select.form.submit();
  });

  /* Countdown (sections) ---------------------------------------------------- */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      const end = Date.parse(this.dataset.end);
      if (Number.isNaN(end)) return;
      const units = {
        d: this.querySelector('[data-unit="d"]'),
        h: this.querySelector('[data-unit="h"]'),
        m: this.querySelector('[data-unit="m"]'),
        s: this.querySelector('[data-unit="s"]')
      };
      const tick = () => {
        let left = Math.max(0, Math.floor((end - Date.now()) / 1000));
        const d = Math.floor(left / 86400); left -= d * 86400;
        const h = Math.floor(left / 3600); left -= h * 3600;
        const m = Math.floor(left / 60);
        const s = left - m * 60;
        const pad = (n) => String(n).padStart(2, '0');
        if (units.d) units.d.textContent = pad(d);
        if (units.h) units.h.textContent = pad(h);
        if (units.m) units.m.textContent = pad(m);
        if (units.s) units.s.textContent = pad(s);
        if (end <= Date.now()) {
          clearInterval(this.timer);
          if (this.dataset.hideOnEnd === 'true') this.closest('.shopify-section').hidden = true;
        }
      };
      tick();
      this.timer = setInterval(tick, 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  if (!customElements.get('slancio-countdown-timer')) customElements.define('slancio-countdown-timer', CountdownTimer);

  /* Reveal on scroll -------------------------------------------------------- */
  if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.documentElement.classList.add('reveal-ready');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    const observe = (root) => root.querySelectorAll('[data-reveal]').forEach((el) => io.observe(el));
    observe(document);
    document.addEventListener('shopify:section:load', (e) => observe(e.target));
  } else {
    document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
  }
})();
